import { useEffect, useMemo, useRef, useState } from 'react';

import {

  Bell,

  Check,

  CheckCheck,

  ChefHat,

  Clock3,

  Loader2,

  ShoppingBag,

  Sparkles,

  Truck,
  XCircle,

} from 'lucide-react';

import { Link, useNavigate } from 'react-router-dom';

import { ordersApi } from '../../api';

import notificationsApi from '../../api/notificationsApi';

import { getApiErrorMessage } from '../../api/axiosClient';

import { useAlerts } from '../feedback/AlertProvider';

import { useAuth } from '../../contexts/AuthContext';

import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/Notifications/role-notifications.css';


const DEFAULT_ESTABLISHMENT_ID = 1;

const ESTABLISHMENT_STORAGE_KEY = 'selected_establishment_id';

const MAX_NOTIFICATIONS = 30;


const MANAGER_ROLES = [

  'restaurantmanager',

  'restaurantmanagerrole',

  'manager',

  'admin',

  'administrator',

  'superadmin',

  'superadministrator',

];


const WAITER_ROLES = ['waiter', 'serveur'];

const KITCHEN_ROLES = ['cuisine', 'kitchen', 'cuisiner', 'cuisinier'];


const ORDER_STATUS_KEYS = [

  'pending',

  'accepted',

  'preparing',

  'ready',

  'delivered',

  'cancelled',

];


const statusIcons = {

  pending: Clock3,

  accepted: Check,

  preparing: ChefHat,

  ready: Truck,

  delivered: CheckCheck,

  cancelled: XCircle,

};


const translateFallback = (t, key, fallback) => {

  const value = t(key);


  return value === key ? fallback : value;

};


const normalizeRole = (role) => {

  return String(role || '')

    .trim()

    .toLowerCase()

    .replace(/[\s_-]/g, '');

};


const normalizeStatus = (status) => {

  const value = String(status || '').trim().toLowerCase();


  return ORDER_STATUS_KEYS.includes(value) ? value : 'pending';

};


const getUserWorkflow = (user) => {

  const role = normalizeRole(user?.role || user?.type || user?.profile?.role);


  if (WAITER_ROLES.includes(role)) return 'waiter';

  if (KITCHEN_ROLES.includes(role)) return 'kitchen';

  if (MANAGER_ROLES.includes(role)) return 'manager';


  return 'manager';

};


const getVisibleStatusesByWorkflow = (workflow) => {

  if (workflow === 'waiter') return ['pending', 'ready'];

  if (workflow === 'kitchen') return ['accepted', 'preparing'];


  return ORDER_STATUS_KEYS;

};


const getOrderReference = (order, t) => {

  if (!order) return '';


  const orderNumber = order.order_number || `#${order.id}`;

  const tableNumber = order.table_number

    ? `${translateFallback(t, 'orders.table.tableNumber', 'Table')} ${order.table_number}`

    : '';

  const customerName = order.customer_name && order.customer_name !== '-'

    ? order.customer_name

    : '';


  return [orderNumber, tableNumber || customerName].filter(Boolean).join(' · ');

};


const buildNotificationTitle = (t, order, eventType) => {

  const status = normalizeStatus(order?.status);


  if (eventType === 'created') {

    if (status === 'accepted') {

      return translateFallback(

        t,

        'orders.notifications.createdAccepted',

        'New accepted table order',

      );

    }


    return translateFallback(t, 'orders.notifications.created', 'New order received');

  }


  return translateFallback(

    t,

    `orders.notifications.status.${status}`,

    translateFallback(t, `orders.status.${status}`, status),

  );

};


const buildNotificationMessage = (t, order) => {

  const reference = getOrderReference(order, t);


  if (!reference) {

    return translateFallback(

      t,

      'orders.notifications.defaultMessage',

      'Open the order to view details.',

    );

  }


  return translateFallback(

    t,

    'orders.notifications.orderReadyMessage',

    'Open the order to view details.',

  ).replace('{{order}}', reference);

};


const createNotificationFromOrder = (order, t, eventType = 'status_updated') => {

  const status = normalizeStatus(order?.status);

  const StatusIcon = statusIcons[status] || ShoppingBag;


  return {

    id: `${order?.id || 'order'}-${status}-${order?.updated_at || order?.created_at || Date.now()}`,

    order,

    order_id: order?.id,

    status,

    event_type: eventType,

    title: buildNotificationTitle(t, order, eventType),

    message: buildNotificationMessage(t, order),

    link: order?.id ? `/orders/${order.id}` : '/orders',

    created_at: order?.updated_at || order?.created_at || new Date().toISOString(),

    read: false,

    icon: StatusIcon,

  };

};


function RoleNotifications() {

  const { t } = useLanguage();

  const navigate = useNavigate();

  const { user } = useAuth();

  const { showAlert } = useAlerts();

  const dropdownRef = useRef(null);


  const [open, setOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const [platformNotifications, setPlatformNotifications] = useState([]);

  const [loadingPlatformNotifications, setLoadingPlatformNotifications] =

    useState(true);


  const userWorkflow = getUserWorkflow(user);

  const visibleStatuses = useMemo(

    () => getVisibleStatusesByWorkflow(userWorkflow),

    [userWorkflow],

  );


  const establishmentId =

    localStorage.getItem(ESTABLISHMENT_STORAGE_KEY) || DEFAULT_ESTABLISHMENT_ID;


  const storageKey = `role_order_notifications_${establishmentId}_${userWorkflow}`;


  const roleTitle = translateFallback(

    t,

    `orders.notifications.roles.${userWorkflow}`,

    userWorkflow,

  );


  const canShowOrder = (order) => {

    if (!order) return false;

    if (userWorkflow === 'manager') return true;


    return visibleStatuses.includes(normalizeStatus(order.status));

  };


  const markAllAsRead = async () => {

    setNotifications((currentNotifications) =>

      currentNotifications.map((notification) => ({

        ...notification,

        read: true,

      })),

    );


    setPlatformNotifications((current) =>

      current.map((notification) => ({

        ...notification,

        read_at:

          notification.read_at ||

          new Date().toISOString(),

      })),

    );


    try {

      await notificationsApi.markAllRead();

    } catch {

      // Order notification read-state remains local.

    }

  };


  const markAsRead = (notificationId) => {

    setNotifications((currentNotifications) =>

      currentNotifications.map((notification) =>

        notification.id === notificationId

          ? {

              ...notification,

              read: true,

            }

          : notification,

      ),

    );

    setOpen(false);

  };


  const fetchPlatformNotifications = async () => {

    try {

      setLoadingPlatformNotifications(true);


      const response =

        await notificationsApi.getNotifications();


      const list =

        Array.isArray(response.data?.data)

          ? response.data.data

          : [];


      setPlatformNotifications(list);

    } catch {

      // Platform notification polling should not break order notifications.

    } finally {

      setLoadingPlatformNotifications(false);

    }

  };


  const markPlatformNotificationRead = async (notificationId) => {

    setPlatformNotifications((current) =>

      current.map((notification) =>

        notification.id === notificationId

          ? {

              ...notification,

              read_at:

                notification.read_at ||

                new Date().toISOString(),

            }

          : notification,

      ),

    );


    try {

      await notificationsApi.markRead(

        notificationId,

      );

    } catch {

      // Local state stays responsive even if marking fails.

    }

  };


  const openPlatformNotification = async (notification) => {

    const kind =

      notification?.data?.kind;


    await markPlatformNotificationRead(

      notification.id,

    );


    setOpen(false);


    if (

      kind ===

      'qr_print_recommendations_ready'

    ) {

      const batchId =

        notification?.data?.batch_id;


      if (batchId) {

        navigate(

          `/qr-print/recommendations/${batchId}`,

        );

      }

    }

  };


  const fetchInitialNotifications = async () => {

    try {

      setLoadingInitial(true);


      const response = await ordersApi.getOrders(establishmentId, {

        per_page: 100,

      });


      const orders = Array.isArray(response.data?.data) ? response.data.data : [];


      const initialNotifications = orders

        .filter(canShowOrder)

        .slice(0, MAX_NOTIFICATIONS)

        .map((order) => createNotificationFromOrder(order, t, 'status_updated'));


      setNotifications((currentNotifications) => {

        const currentById = currentNotifications.reduce((items, notification) => {

          return {

            ...items,

            [notification.id]: notification,

          };

        }, {});


        return initialNotifications.map((notification) => ({

          ...notification,

          read: currentById[notification.id]?.read || false,

        }));

      });

    } catch (error) {

      showAlert({

        type: 'error',

        title: t('alerts.error'),

        message: getApiErrorMessage(error),

      });

    } finally {

      setLoadingInitial(false);

    }

  };


  useEffect(() => {

    const savedNotifications = localStorage.getItem(storageKey);


    if (!savedNotifications) return;


    try {

      const parsedNotifications = JSON.parse(savedNotifications);


      if (Array.isArray(parsedNotifications)) {

        setNotifications(parsedNotifications);

      }

    } catch {

      localStorage.removeItem(storageKey);

    }

  }, [storageKey]);


  useEffect(() => {

    localStorage.setItem(

      storageKey,

      JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS)),

    );

  }, [notifications, storageKey]);


  useEffect(() => {
    fetchInitialNotifications();

    const interval = window.setInterval(() => {
      fetchInitialNotifications();
    }, 6000);

    return () => {
      window.clearInterval(interval);
    };
  }, [establishmentId, userWorkflow]);


  useEffect(() => {

    fetchPlatformNotifications();


    const interval = window.setInterval(

      fetchPlatformNotifications,

      12000,

    );


    return () => {

      window.clearInterval(interval);

    };

  }, [user?.id]);


  useEffect(() => {

    if (!open) return undefined;


    const handleClickOutside = (event) => {

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {

        setOpen(false);

      }

    };


    const handleEscape = (event) => {

      if (event.key === 'Escape') {

        setOpen(false);

      }

    };


    document.addEventListener('mousedown', handleClickOutside);

    document.addEventListener('keydown', handleEscape);


    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

      document.removeEventListener('keydown', handleEscape);

    };

  }, [open]);


  const orderUnreadCount = notifications.filter(

    (notification) => !notification.read,

  ).length;


  const platformUnreadCount = platformNotifications.filter(

    (notification) => !notification.read_at,

  ).length;


  const unreadCount =

    orderUnreadCount +

    platformUnreadCount;
  return (

    <div

      ref={dropdownRef}

      className="role-notifications"

    >

      <button

        type="button"

        className="role-notifications__trigger"

        onClick={() => setOpen((currentOpen) => !currentOpen)}

        aria-label={translateFallback(

          t,

          'orders.notifications.open',

          'Open notifications',

        )}

      >

        <Bell size={19} />


        {unreadCount > 0 && (

          <span className="role-notifications__badge">

            {unreadCount > 9 ? '9+' : unreadCount}

          </span>

        )}

      </button>


      {open && (

        <section className="role-notifications__dropdown">

          <header className="role-notifications__head">

            <div>

              <p>{t('notifications.global')}</p>

              <h2>{translateFallback(t, 'orders.notifications.title', 'Notifications')}</h2>

            </div>




          </header>


          <div className="role-notifications__actions">

            <Link

              to="/notifications"

              onClick={() => setOpen(false)}

            >

              {t('notifications.viewAll')}

            </Link>


            <button type="button" onClick={markAllAsRead}>

              <CheckCheck size={14} />

              {translateFallback(t, 'orders.notifications.markAllRead', 'Mark all read')}

            </button>

          </div>


          <div className="role-notifications__list">

            {platformNotifications.length > 0 && (

              <div className="role-notifications__platform-section">

                <span className="role-notifications__section-title">

                  {t('notifications.platform')}

                </span>


                {platformNotifications.map((notification) => {

                  const isQrReady =

                    notification?.data?.kind ===

                    'qr_print_recommendations_ready';


                  return (

                    <button

                      key={notification.id}

                      type="button"

                      className={`role-notifications__item role-notifications__item--platform ${

                        notification.read_at

                          ? ''

                          : 'role-notifications__item--unread'

                      }`}

                      onClick={() =>

                        openPlatformNotification(

                          notification,

                        )

                      }

                    >

                      <span className="role-notifications__item-icon">

                        {isQrReady ? (

                          <Sparkles size={17} />

                        ) : (

                          <Bell size={17} />

                        )}

                      </span>


                      <span className="role-notifications__item-body">

                        <strong>

                          {isQrReady

                            ? t(

                                'notifications.types.qrReady.title',

                              )

                            : notification?.data?.title ||

                              t(

                                'notifications.defaultTitle',

                              )}

                        </strong>


                        <small>

                          {isQrReady

                            ? t(

                                'notifications.types.qrReady.message',

                              )

                            : notification?.data?.message ||

                              ''}

                        </small>

                      </span>


                      {!notification.read_at && (

                        <em className="role-notifications__unread-dot" />

                      )}

                    </button>

                  );

                })}

              </div>

            )}


            {loadingInitial && !notifications.length ? (

              <div className="role-notifications__loading">

                <Loader2 size={20} />

                {translateFallback(

                  t,

                  'orders.notifications.loading',

                  'Loading notifications...',

                )}

              </div>

            ) : (

              notifications.map((notification) => {

                const NotificationIcon =

                  statusIcons[notification.status] || ShoppingBag;


                return (

                  <Link

                    key={notification.id}

                    to={notification.link || '/orders'}

                    className={`role-notifications__item ${

                      notification.read ? '' : 'role-notifications__item--unread'

                    } role-notifications__item--${notification.status}`}

                    onClick={() => markAsRead(notification.id)}

                  >

                    <span className="role-notifications__item-icon">

                      <NotificationIcon size={17} />

                    </span>


                    <span className="role-notifications__item-body">

                      <strong>

                        {notification.order

                          ? buildNotificationTitle(

                              t,

                              notification.order,

                              notification.event_type,

                            )

                          : notification.title}

                      </strong>


                      <small>

                        {notification.order

                          ? buildNotificationMessage(

                              t,

                              notification.order,

                            )

                          : notification.message}

                      </small>

                    </span>


                    {!notification.read && (

                      <em className="role-notifications__unread-dot" />

                    )}

                  </Link>

                );

              })

            )}


            {!loadingInitial &&

            !loadingPlatformNotifications &&

            !notifications.length &&

            !platformNotifications.length && (

              <div className="role-notifications__empty">

                <ShoppingBag size={24} />

                <p>

                  {translateFallback(

                    t,

                    'orders.notifications.empty',

                    'No notifications for now.',

                  )}

                </p>

              </div>

            )}

          </div>

        </section>

      )}


    </div>

  );

}


export default RoleNotifications;